import {Assert, GetRandomNumber} from "js-vextensions";
import {AssertNotify, O, RunInAction} from "web-vcore";
import {EngineConfig_VR, ImageFrameFlawType} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import {ParseImagePath} from "../../../../Utils/Bridge/Bridge_Native/MediaTypes.js";
import {Remote_GetInfoForNewFrame} from "../../../../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {VideoClip, rtcClient} from "../../../../Utils/Bridge/Bridge_WebRTC/RTCClient.js";
import {store} from "../../../index.js";
import {action} from "mobx";
import {Log_Direct} from "../../../../Utils/General/Logging.js";

export class ImageFrame {
	static lastId = 0;

	constructor(data?: Partial<Omit<ImageFrame, "id">>) {
		
		Object.assign(this, data);
		this.id = ++ImageFrame.lastId;
	}
	id: number;
	/** The global imageFrameOffset at which the image first shows up, at its farthest distance. */
	startOffset: number;
	EndOffset(config: EngineConfig_VR) {
		return this.startOffset + config.frames_duration;
	}

	@action Cleanup() {
		this.frameData_textureStr = null;
		this.frameData_videoClip = null;
		const videoEl = this.frameData_videoClip_player;
		if (videoEl) {
			// some extra cleanup, based on: https://stackoverflow.com/q/3258587
			// (not sure if necessary nowadays; haven't directly tested, since I added this before the "key/success-reaching" fix [of no longer logging the VideoClip object to console])
			videoEl.pause(); // probably unnecessary
			videoEl.src = ""; // clear the src, so that the video-player can be garbage-collected (seemingly GC bug in chrome)
			videoEl.load(); // probably unnecessary

			videoEl.remove();
		}
		this.frameData_videoClip_player = null;
		this.cleanupDone = true; // mark as done, so we don't do the cleanup again
	}

	@O accessor poolName: string|n; // the pool from which this image was acquired
	@O accessor pool_lockInCountRemaining: number|n;
	@O accessor targetInitials: string|n;

	@O accessor texturePath: string|n;
	@O accessor frameData_textureStr: string|n;

	@O accessor videoPath: string|n;
	@O accessor videoDuration: number|n; // in ms
	@O accessor frameData_videoClip: VideoClip|n; // if set, then the frame-data is a video (and the frameData_textureStr is not used)
	@O accessor frameData_videoClip_player: HTMLVideoElement|n;

	async AcquireFrameDataAndResolveMetadata(config: EngineConfig_VR, priorSiblingFrame: ImageFrame|n) {
		const vrSettings = store.main.settings.vr;
		try {
			let poolToSelect: string|n;
			let poolToExclude: string|n;
			const lockInRemainingAfterPrevFrame = priorSiblingFrame?.pool_lockInCountRemaining ?? 0;
			if (priorSiblingFrame?.poolName) { // (if previous frame has no pool-name associated, then we can't apply any series select/exclude based on it anyway)
				if (lockInRemainingAfterPrevFrame > 0) {
					poolToSelect = priorSiblingFrame.poolName;
				} else {
					const prevPool = config.frames_pools.find(a=>a.name == priorSiblingFrame.poolName);
					if (prevPool?.lockIn_forceSwitchAfter) {
						poolToExclude = priorSiblingFrame.poolName;
					}
				}
			}

			console.log(`Acquire STARTED of info for image-frame #${this.id}:`, this);
			const newFrameInfo = await Remote_GetInfoForNewFrame(vrSettings.imageFrame_transferSize, vrSettings.imageFrame_transferQuality, vrSettings.imageFrame_timeoutForResend, poolToSelect, poolToExclude);
			console.log(`Acquire DONE of info for image-frame #${this.id}:`, newFrameInfo);
			if (newFrameInfo == null) return void console.warn("Found no frame-images in folder:", config.frames_imageFolder);
			const pool = config.frames_pools.find(a=>a.name == newFrameInfo.poolName);

			RunInAction("ImageFrame.AcquireFrameDataAndResolveMetadata.part1", ()=>{
				this.poolName = newFrameInfo.poolName;
				this.targetInitials = newFrameInfo.targetInitials;
				this.texturePath = newFrameInfo.imagePath;
				this.frameData_textureStr = newFrameInfo.imageStr;
				this.videoPath = newFrameInfo.videoPath;
				this.videoDuration = newFrameInfo.videoDuration;

				if (pool == null) {
					AssertNotify(false, `ImageFrame pool not found: ${this.poolName}`);
					return;
				}
				this.pool_lockInCountRemaining = lockInRemainingAfterPrevFrame > 0 ? lockInRemainingAfterPrevFrame - 1 : GetRandomNumber({min: pool.lockIn_min, max: pool.lockIn_max, mustBeInteger: true}) - 1;

				this.lie = pool.flaw_type == ImageFrameFlawType.poolChanged
					? (priorSiblingFrame?.poolName != null && this.poolName != priorSiblingFrame?.poolName)
					: Math.random() < pool.flaw_percent;
				if (this.lie) {
					const initialsOfTrueCharsInFrame = this.texturePath ? ParseImagePath(this.texturePath).characterInitials : [];
					const charsNotInFrame = pool.characters.filter(c=>!initialsOfTrueCharsInFrame.includes(c.initials));
					this.lie_initialsOfLieChar = charsNotInFrame.length ? charsNotInFrame.map(c=>c.initials).Random() : NO_OTHER_CHARACTERS_FOUND;
				}
			});

			// if image-str is null, then frame-data must be a video; request that now
			if (this.videoPath) {
				Assert(pool?.videoClip_duration != null, `Pool video-clip-duration not set for pool: ${this.poolName}`);

				let clipStartTime_s: number;
				const texturePathInfo = this.texturePath ? ParseImagePath(this.texturePath) : null;
				// if an image was already selected, and it specifies a time-str, then use that to get the video-clip at the same time
				if (texturePathInfo?.timeStr_asSeconds != null) {
					clipStartTime_s = texturePathInfo.timeStr_asSeconds;
				}
				// else, get a random video-time from the selected video (that is valid for use as a clip start-time)
				else {
					const fullVideoDuration_s = this.videoDuration! / 1000; // convert from ms to seconds
					// have interval be half the clip-duration, so that every moment in the video is covered twice (once in first half of a clip, once in second half -- ensuring user's ability to reconstruct order of all clips/events)
					const startTimeInterval = pool.videoClip_duration / 2;

					const validStartTimes = [] as number[];
					// stop iteration when video time-left drops to half the target-clip-duration (this way we avoid a really short final clip -- but ensure the last moment does still get covered [in the second-half of the 2nd-last clip])
					for (let time = 0; time < (fullVideoDuration_s - (pool.videoClip_duration / 2)); time += startTimeInterval) {
						validStartTimes.push(time);
					}
					clipStartTime_s = validStartTimes.Random();
				}

				const clip = await rtcClient.GetVideoClip({
					videoPath: this.videoPath,
					clipStartTime: clipStartTime_s,
					clipDuration: pool.videoClip_duration,
					audioTrack_preferredLanguage: pool.videoClip_audioTrack_preferredLanguage ?? "",
				});
				Log_Direct("Acquired video-clip for image-frame:", this.id); //, clip); // LOGGING CLIP IS MEMORY LEAK!
				RunInAction("ImageFrame.AcquireFrameDataAndResolveMetadata.part2", ()=>{
					this.frameData_videoClip = clip;
				});
			}
		} catch (err) {
			// failing to acquire a texture happens pretty regularly (textures are large and can fail to send), so only log a warning rather than show an error-notification
			console.warn("Error acquiring frame-data and resolving metadata for image-frame. @frame:", this, "@error:", err);
		}
	}

	lie = false;
	lie_initialsOfLieChar?: string|n;

	// interaction status
	@O accessor currentMemoryPromptInfo: MemoryPalacePromptInfo|n;
	userMarkedAsLie = false;
	toldRemoteOfDisplay = false;
	effectsPlayed = false;
	cleanupDone = false; // whether the frame has been cleaned up (ie. its "heavy" data cleared) after being displayed
}
export const NO_OTHER_CHARACTERS_FOUND = "NO_OTHER_CHARACTERS_FOUND";

// similar (but not same) structure as MemoryPalace_RecallResult
export class MemoryPalacePromptInfo {
	constructor(data?: Partial<MemoryPalacePromptInfo>) {
		
		Object.assign(this, data);
	}
	@O accessor actionType: "find"|"move";
	@O accessor target: string;
	@O accessor time_decodeStart: number;
	@O accessor time_doneWithDecodeAttempt: number | undefined;
	@O accessor time_decodeMarkedAsFailed: number | undefined;
	@O accessor time_doneWithNextEncode: number | undefined;
}